import {
  Award,
  BarChart3,
  Clapperboard,
  Building2,
  DollarSign,
  Flag,
  Gauge,
  Gift,
  Globe,
  Home,
  Inbox,
  LayoutTemplate,
  LifeBuoy,
  MessageSquare,
  Paintbrush,
  Palette,
  QrCode,
  Receipt,
  Shield,
  ShoppingBag,
  Sparkles,
  Sticker,
  Store,
  Tags,
  UserX,
  type LucideIcon,
} from "lucide-react";

/**
 * CMS menu configuration (C-CMS-DS, plans/ps-cms-shell.md T3) — pure data.
 *
 * Menu ids are the keys of `ADMIN_ONLY_MENU_IDS` (`hooks/usePermissions.ts`) and
 * `BACKEND_PENDING_MENU_IDS` (`config/backendPending.ts`); never rename one without
 * both. Visibility is the two-axis rule `canAccessMenu(id) && !isBackendPending(id)`
 * applied by the sidebar and the command palette. Group labels are written in
 * title case and rendered uppercase by CSS.
 */
export type NavItem = {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  matchPrefixes?: string[];
};

export type NavGroup = {
  id: string;
  label: string | null;
  items: NavItem[];
  collapsible?: { id: string; label: string; icon: LucideIcon };
};

export const NAV_GROUPS: NavGroup[] = [
  {
    id: "main",
    label: null,
    items: [{ id: "dashboard", label: "Dashboard", path: "/dashboard", icon: Home }],
  },
  {
    id: "customers",
    label: "Customers",
    items: [
      { id: "organizations", label: "Organizations", path: "/organizations", icon: Building2, matchPrefixes: ["/organizations"] },
      { id: "requests", label: "Bookings", path: "/bookings", icon: Inbox, matchPrefixes: ["/bookings"] },
      { id: "join-links", label: "Join links", path: "/join-links", icon: QrCode, matchPrefixes: ["/join-links"] },
    ],
  },
  {
    id: "marketplace",
    label: "Marketplace",
    items: [
      { id: "vendors", label: "Vendors", path: "/vendors", icon: Store },
      { id: "marketplace", label: "Services", path: "/marketplace", icon: ShoppingBag },
      { id: "service-categories", label: "Service categories", path: "/service-categories", icon: Tags },
    ],
  },
  {
    id: "perks",
    label: "Perks",
    collapsible: { id: "perks-menu", label: "Perks", icon: Gift },
    items: [
      { id: "perks", label: "All perks", path: "/perks", icon: Gift, matchPrefixes: ["/perks"] },
      { id: "perk-categories", label: "Categories", path: "/perk-categories", icon: Tags },
      { id: "perk-locations", label: "Locations", path: "/perk-locations", icon: Flag },
    ],
  },
  {
    id: "employee-app",
    label: "Employee app",
    items: [
      { id: "help", label: "Help Center", path: "/help-center", icon: LifeBuoy },
      { id: "engagement", label: "Engagement score", path: "/engagement-score", icon: Gauge },
      { id: "app-feedback", label: "App feedback", path: "/app-feedback", icon: MessageSquare },
      { id: "feed-stickers", label: "Feed stickers", path: "/feed-stickers", icon: Sticker },
      { id: "app-splashes", label: "Splash screens", path: "/app-splashes", icon: Clapperboard, matchPrefixes: ["/app-splashes"] },
      { id: "kudos-types", label: "Kudos types", path: "/kudos-types", icon: Award },
    ],
  },
  {
    id: "events",
    label: "Events",
    collapsible: { id: "activations-menu", label: "Activations", icon: Sparkles },
    items: [
      { id: "activations", label: "Events", path: "/activations/events", icon: Sparkles, matchPrefixes: ["/activations/events"] },
      {
        id: "activation-templates",
        label: "Templates",
        path: "/activations/templates",
        icon: LayoutTemplate,
        matchPrefixes: ["/activations/templates"],
      },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    items: [
      { id: "financial", label: "Financial operations", path: "/financial-operations", icon: DollarSign },
      { id: "billing", label: "Billing", path: "/billing", icon: Receipt },
      { id: "analytics", label: "Analytics", path: "/analytics", icon: BarChart3 },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    items: [
      { id: "customizations", label: "Tenant customizations", path: "/tenant-customizations", icon: Paintbrush },
      { id: "localization", label: "Localization", path: "/localization", icon: Globe },
      { id: "admin", label: "System administration", path: "/system-administration", icon: Shield },
      { id: "account-deletions", label: "Account deletions", path: "/account-deletions", icon: UserX },
      { id: "design-system", label: "Design system", path: "/design-system", icon: Palette },
    ],
  },
];

/** Every item of every group, flattened in menu order. */
export const ALL_NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

/**
 * The longest of the item's paths (`path` + `matchPrefixes`) that the location equals
 * or sits under; `null` when none matches.
 */
export function matchedNavPath(item: NavItem, location: string): string | null {
  let best: string | null = null;
  for (const p of [item.path, ...(item.matchPrefixes ?? [])]) {
    if ((location === p || location.startsWith(p + "/")) && p.length > (best?.length ?? -1)) best = p;
  }
  return best;
}

function matchLength(item: NavItem, location: string): number {
  return matchedNavPath(item, location)?.length ?? -1;
}

/** Whether `location` is the item's page or one of its sub-pages. */
export function isActive(item: NavItem, location: string): boolean {
  return matchLength(item, location) >= 0;
}

/** The id of the item to light for `location` — the longest matching path wins; `null` when none. */
export function getActiveNavItemId(location: string, items: NavItem[] = ALL_NAV_ITEMS): string | null {
  let bestId: string | null = null;
  let bestLength = -1;
  for (const item of items) {
    const length = matchLength(item, location);
    if (length > bestLength) {
      bestLength = length;
      bestId = item.id;
    }
  }
  return bestId;
}
